import { useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from "react"
import { Outlet, createRoute, useRouter, useRouterState, type AnyRoute, type AnyRouter } from "@tanstack/react-router"

import { ApplicationContext } from "./application-context"
import { ApplicationScopeProvider, useApplication } from "./application-provider"
import { ApplicationPluginUnauthorizedError, runApplicationPreload, type Application } from "./application"
import { createApplicationPluginRouteStaticData, getApplicationPluginRouteId } from "./application-plugin-route"
import type { ApplicationRouteEntry } from "./application-route"
import type { ApplicationPlugin } from "./composition"
import type { ApplicationScope } from "./plugin-runtime"

export interface CreateApplicationPluginRoutesOptions {
  readonly application: Application
  readonly parentRoute: AnyRoute
}

interface PluginRouteContext {
  readonly applicationScope?: ApplicationScope
  readonly applicationAccessContextRevision?: number
}

const revalidatedRevisions = new WeakMap<AnyRouter, number>()

/** A retained Router may outlive ApplicationProvider's keyed access boundary. */
function useApplicationRouteAccessRevision(application: Application): number {
  const router = useRouter()
  const revision = useSyncExternalStore(
    (listener) => application.session.subscribeAccessContext(listener),
    () => application.session.accessContext.revision,
    () => application.session.accessContext.revision
  )
  const stale = useRouterState({
    select: (state) =>
      state.matches.some((match) => {
        const context = match.context as PluginRouteContext
        return (
          getApplicationPluginRouteId(match.staticData) != null &&
          context.applicationAccessContextRevision != null &&
          context.applicationAccessContextRevision !== revision
        )
      }),
  })
  useEffect(() => {
    if (!stale || revalidatedRevisions.get(router) === revision) return
    // Multiple nested boundaries/workspaces can observe the same identity change.
    revalidatedRevisions.set(router, revision)
    void router.invalidate().catch((error) =>
      application.telemetry.reportError(error, {
        "florisoft.error.source": "route-access-revalidation",
      })
    )
  }, [application, router, revision, stale])
  return revision
}

export type ApplicationRouteState =
  | {
      readonly status: "loading"
      readonly location: string
    }
  | {
      readonly status: "not-found"
      readonly location: string
    }
  | {
      readonly status: "error"
      readonly location: string
      readonly error: unknown
    }
  | {
      readonly status: "ready"
      readonly location: string
      readonly scope: ApplicationScope
    }

/**
 * Creates pathless TanStack Router boundaries for the routed plugins in an
 * application's selected plugin set. Plugin route factories still create ordinary
 * TanStack routes; the framework boundary only owns authorization, lazy
 * plugin activation and ApplicationScope propagation.
 */
export function createApplicationPluginRoutes({
  application,
  parentRoute,
}: CreateApplicationPluginRoutesOptions): readonly AnyRoute[] {
  const childrenByParent = new Map<string | null, ApplicationPlugin[]>()

  for (const plugin of application.plugins) {
    const children = childrenByParent.get(plugin.parentId ?? null) ?? []
    children.push(plugin)
    childrenByParent.set(plugin.parentId ?? null, children)
  }

  const hasRoutedSubtree = (plugin: ApplicationPlugin): boolean => {
    if (plugin.routes != null) return true
    return (childrenByParent.get(plugin.id) ?? []).some(hasRoutedSubtree)
  }

  const createPluginBoundary = (plugin: ApplicationPlugin, routeParent: AnyRoute): AnyRoute => {
    const boundaryRoute = createRoute({
      getParentRoute: () => routeParent,
      id: `florisoft-plugin-${encodeURIComponent(plugin.id)}`,
      staticData: createApplicationPluginRouteStaticData(plugin.id),
      beforeLoad: async ({ preload, abortController }) => {
        const scope = await activateApplicationRoute(application, plugin, {
          preload,
          signal: abortController.signal,
        })
        if (scope == null) {
          // Authentication cancellation is navigation cancellation, not a route error.
          abortController.abort()
          return
        }
        return {
          applicationScope: scope,
          applicationAccessContextRevision: application.session.accessContext.revision,
        }
      },
      component: () => {
        const revision = useApplicationRouteAccessRevision(application)
        const { applicationScope, applicationAccessContextRevision } =
          boundaryRoute.useRouteContext() as PluginRouteContext
        // Never mount an old account's page, even for the frame before invalidation.
        if (applicationScope == null || applicationAccessContextRevision !== revision) return null
        return (
          <ApplicationScopeProvider scope={applicationScope}>
            <Outlet />
          </ApplicationScopeProvider>
        )
      },
    })

    const ownRoutes = plugin.routes?.({ parentRoute: boundaryRoute }) ?? []
    const childPluginRoutes = (childrenByParent.get(plugin.id) ?? [])
      .filter(hasRoutedSubtree)
      .map((child) => createPluginBoundary(child, boundaryRoute))

    return boundaryRoute.addChildren([...ownRoutes, ...childPluginRoutes])
  }

  return (childrenByParent.get(null) ?? [])
    .filter(hasRoutedSubtree)
    .map((plugin) => createPluginBoundary(plugin, parentRoute))
}

/** Authentication is interactive only during navigation, never during preloading. */
export async function activateApplicationRoute(
  application: Application,
  plugin: ApplicationPlugin | string,
  options: { readonly preload?: boolean; readonly signal?: AbortSignal } = {}
): Promise<ApplicationScope | null> {
  options.signal?.throwIfAborted()
  try {
    const scope = await (options.preload
      ? runApplicationPreload(application, () => application.activatePlugin(plugin))
      : application.activatePlugin(plugin))
    options.signal?.throwIfAborted()
    return scope
  } catch (error) {
    if (
      !(error instanceof ApplicationPluginUnauthorizedError) ||
      options.preload ||
      application.session.status === "signed-in"
    )
      throw error
    const authentication = await application.requestAuthentication(options.signal)
    if (authentication === "cancelled") return null
    if (authentication !== "authenticated") throw error
    options.signal?.throwIfAborted()
    // Retry once; a signed-in user lacking permission must receive authorization failure.
    const scope = await application.activatePlugin(plugin)
    options.signal?.throwIfAborted()
    return scope
  }
}

/**
 * Returns the scope established by the currently matched plugin route.
 * `null` means a plugin route matched but its authorization/activation boundary
 * is still loading. Non-plugin routes use the application's root scope.
 */
export function useApplicationRouteScope(): ApplicationScope | null {
  const routeState = useApplicationRouteState()
  return routeState.status === "ready" ? routeState.scope : null
}

export function useApplicationRouteState(): ApplicationRouteState {
  const application = useApplication()
  const revision = useApplicationRouteAccessRevision(application)

  return useRouterState({
    select: (state) => {
      const location = state.location.href
      for (let index = state.matches.length - 1; index >= 0; index -= 1) {
        const match = state.matches[index]
        if (match.status === "notFound" || match._notFound) {
          return {
            status: "not-found" as const,
            location,
          }
        }
        if (match.status === "error") {
          return {
            status: "error" as const,
            location,
            error: match.error,
          }
        }
      }

      if (state.resolvedLocation?.href !== state.location.href) {
        return {
          status: "loading" as const,
          location,
        }
      }

      if (
        state.matches.some((match) => {
          const pluginId = getApplicationPluginRouteId(match.staticData)
          return pluginId != null && (match.context as PluginRouteContext).applicationAccessContextRevision !== revision
        })
      ) {
        return { status: "loading" as const, location }
      }

      for (let index = state.matches.length - 1; index >= 0; index -= 1) {
        const match = state.matches[index]
        const pluginId = getApplicationPluginRouteId(match.staticData)
        if (pluginId == null) continue

        if (match.status !== "success") {
          return {
            status: "loading" as const,
            location,
          }
        }

        const context = match.context as PluginRouteContext
        const scope = context.applicationScope
        if (scope?.id !== pluginId) {
          return {
            status: "loading" as const,
            location,
          }
        }

        return {
          status: "ready" as const,
          location,
          scope,
        }
      }

      return {
        status: "ready" as const,
        location,
        scope: application.rootScope,
      }
    },
  })
}

/**
 * Keeps the route entries the current user may open. While signed in, an entry owned by a plugin
 * stays only while that plugin is authorized, and follows login, logout and server changes. Without
 * a signed-in session every entry stays, because opening one presents the shared login challenge.
 * Entries without an owning plugin are kept, as are all entries outside ApplicationProvider.
 * Opening a hidden route directly still fails with the plugin's authorization error.
 */
export function useAuthorizedApplicationRoutes(
  routes: readonly ApplicationRouteEntry[]
): readonly ApplicationRouteEntry[] {
  const application = useContext(ApplicationContext)
  const subscribe = useCallback(
    (listener: () => void) => {
      if (application == null) return () => undefined
      // Plugin authorization reads the authenticated Root, the session and its access context.
      const unsubscribes = [
        application.subscribeAuthenticatedState(listener),
        application.session.subscribe(listener),
        application.session.subscribeAccessContext(listener),
      ]
      return () => {
        for (const unsubscribe of unsubscribes) unsubscribe()
      }
    },
    [application]
  )
  const getAuthorizedPluginIds = () =>
    application == null || application.session.status !== "signed-in"
      ? null
      : JSON.stringify(
          application.plugins.filter((plugin) => application.isPluginAuthorized(plugin)).map((plugin) => plugin.id)
        )
  const authorizedPluginIds = useSyncExternalStore(subscribe, getAuthorizedPluginIds, getAuthorizedPluginIds)

  return useMemo(() => {
    if (authorizedPluginIds == null) return routes
    const authorized = new Set(JSON.parse(authorizedPluginIds) as string[])
    return routes.filter((route) => route.pluginId == null || authorized.has(route.pluginId))
  }, [routes, authorizedPluginIds])
}
